import { buildDisbursement } from "./disbursements.js";
import { manualRequestPayload } from "./fileRequests.js";

// A submitted travel claim, on the Disbursements page and in the Aid Planner (Emma, 29 Oct 2026).
//
// Disbursements: one entry per client and aid number, so a claim for two clients is two
// entries. Each entry lists its expenses - what each one is for (Accommodation, Flights...),
// its fee and its receipts - so a claim with several kinds of expense is still told apart.
//
// Aid Planner: a VLA-funded flight needs VLA's approval, so each client with one gets an
// extension request in the Aid Planner's request list.
//
// `claim` is { claimId, employee, dateOfRequest, lines }, each line
// { clientName, aidRef, hasVla, type, detail, date, amount, receipts: [{ name, url }] }.

const clean = (value, max) => String(value ?? "").trim().slice(0, max);
const money = (value) => `$${(Number(value) || 0).toFixed(2)}`;

function groupByClient(lines = []) {
  const groups = new Map();
  for (const line of lines) {
    const clientName = clean(line.clientName, 120);
    const aidRef = clean(line.aidRef, 60);
    const key = `${clientName.toLowerCase()}|${aidRef.toLowerCase()}`;
    if (!groups.has(key)) groups.set(key, { clientName, aidRef, lines: [] });
    groups.get(key).lines.push(line);
  }
  return [...groups.values()];
}

export function travelClaimLine(line = {}) {
  return {
    type: clean(line.type, 60) || "Expense",
    detail: clean(line.detail, 200),
    date: clean(line.date, 10),
    amount: Math.round((Number(line.amount) || 0) * 100) / 100,
    receipts: (Array.isArray(line.receipts) ? line.receipts : [])
      .filter((receipt) => receipt?.url)
      .slice(0, 20)
      .map((receipt) => ({ name: clean(receipt.name, 200) || "Receipt", url: clean(receipt.url, 500) })),
  };
}

export function travelClaimDisbursements({ claimId, employee = "", dateOfRequest = "", lines = [] } = {}, meta = {}) {
  return groupByClient(lines).map((group, index) => {
    const travelLines = group.lines.map(travelClaimLine).slice(0, 30);
    const types = [...new Set(travelLines.map((line) => line.type))];
    const firstReceipt = travelLines.flatMap((line) => line.receipts)[0];
    return buildDisbursement({
      type: "travel",
      clientName: group.clientName,
      aidRef: group.aidRef,
      supplier: `Travel claim - ${clean(employee, 100) || "staff"}`,
      invoiceDate: /^\d{4}-\d{2}-\d{2}$/.test(dateOfRequest) ? dateOfRequest : "",
      description: types.join(", "),
      amount: travelLines.reduce((sum, line) => sum + line.amount, 0).toFixed(2),
      fileName: firstReceipt?.name || "",
      fileUrl: firstReceipt?.url || "",
      source: "travel_claim",
      travelLines,
    }, { ...meta, id: `travel-${claimId}-${index + 1}` });
  });
}

// The extension requests for a claim's VLA-funded flights, as Aid Planner request records.
export function flightExtensionRequests({ claimId, employee = "", lines = [] } = {}, brand = "lacw", now = new Date()) {
  const flights = lines.filter((line) => line.type === "Flights" && line.hasVla === "yes");
  return groupByClient(flights).map((group, index) => {
    const total = group.lines.reduce((sum, line) => sum + (Number(line.amount) || 0), 0);
    const detail = [
      `Extension request for flights - from ${clean(employee, 100) || "staff"}'s travel claim.`,
      `Client: ${group.clientName || "not given"}`,
      `Aid number: ${group.aidRef || "not given"}`,
      ...group.lines.map((line) => `Flights${line.date ? ` ${line.date}` : ""}${line.detail ? ` (${line.detail})` : ""}: ${money(line.amount)}`),
      `Total: ${money(total)}`,
    ].join("\n");
    const payload = manualRequestPayload({ clientName: group.clientName || "Unknown client" }, brand, now);
    return {
      ...payload,
      draft_name: `Extension request - ${group.clientName || "client"}`,
      form_data: {
        ...payload.form_data,
        message_id: `travel_${claimId}_${index + 1}`,
        subject: `Extension request: flights - ${group.clientName || "client"}${group.aidRef ? ` (${group.aidRef})` : ""}`,
        snippet: detail.split("\n").slice(1).join(" • "),
        body: detail,
        category: "Extension App",
        client_name: group.clientName,
        aid_ref: group.aidRef,
      },
    };
  });
}
